import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { getPartsTree } from "../../repositories/RoofRepository";
import {
  PART_IDS,
  transformAPIToTreeData,
  type PartsTreeRowAPI,
} from "../../models";
/**
 * Query hook for the Roof parts tree.
 *
 * Fetches both online and offline roofs in parallel,
 * merges and transforms them into RoofTreeRow[] for the tree panel.
 *
 * Query key: ["roof-tree"]
 */
export const useRoofPartsTreeQuery = () => {
  return useQuery({
    queryKey: ["roof-tree"],
    meta: { realtime: ["RBD.EQPGetPartsTree"] },
    refetchInterval: 10_000,
    queryFn: async () => {
      const [onlineResponse, offlineResponse] = await Promise.all([
        getPartsTree(PART_IDS.EAF_ONLINE),
        getPartsTree(PART_IDS.EAF_OFFLINE),
      ]);

      let allRows: PartsTreeRowAPI[] = [];

      if (onlineResponse.ok) {
        const onlineRows: PartsTreeRowAPI[] = get(
          onlineResponse,
          "data.tables[0].rows",
          [],
        );
        allRows = [...onlineRows];
      }

      if (offlineResponse.ok) {
        const offlineRows: PartsTreeRowAPI[] = get(
          offlineResponse,
          "data.tables[0].rows",
          [],
        );
        allRows = [...allRows, ...offlineRows];
      }

      if (!onlineResponse.ok && !offlineResponse.ok) {
        const errorMsg =
          onlineResponse.message ??
          offlineResponse.message ??
          "Failed to load parts tree";
        throw new Error(errorMsg);
      }

      return transformAPIToTreeData(allRows);
    },
  });
};
